/**
 * SortableTableHeader — renders a <th> that toggles asc/desc/none on click.
 * Usage:
 *   <SortableTableHeader label="Category Code" field="code" sortState={sortState} onSort={handleSort} />
 *
 * sortState: { field: string, dir: 'asc' | 'desc' }
 * onSort: (field, dir) => void
 */
import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export function SortableTableHeader({ label, field, sortState, onSort, className = '', style }) {
  const isActive = sortState?.field === field;
  const dir = isActive ? sortState.dir : null;

  const handleClick = () => {
    if (!isActive || dir === 'desc') onSort(field, 'asc');
    else if (dir === 'asc') onSort(field, 'desc');
  };

  return (
    <th
      className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap select-none ${className}`}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      scope="col"
      style={style}
    >
      <button
        onClick={handleClick}
        className={`flex items-center gap-1 hover:text-foreground transition-colors ${
          isActive ? 'text-primary' : ''
        } ${className.includes('text-right') ? 'ml-auto' : ''}`}
        aria-label={`Sort by ${label} ${
          !isActive ? 'ascending' : dir === 'asc' ? 'descending' : 'ascending'
        }`}
      >
        <span>{label}</span>
        <span className="flex-shrink-0">
          {!isActive && <ChevronsUpDown className="h-3 w-3 opacity-40" />}
          {isActive && dir === 'asc'  && <ChevronUp   className="h-3 w-3" />}
          {isActive && dir === 'desc' && <ChevronDown className="h-3 w-3" />}
        </span>
      </button>
    </th>
  );
}

export function useSortState(defaultField = '', defaultDir = 'asc') {
  const [sortState, setSortState] = React.useState({ field: defaultField, dir: defaultDir });
  const handleSort = (field, dir) => setSortState({ field, dir });
  return [sortState, handleSort];
}

/**
 * Generic client-side sort for arrays of objects.
 * Handles numbers, strings, dates, booleans and nulls gracefully.
 */
export function sortData(data, key, dir) {
  if (!key || !data) return data || [];
  return [...data].sort((a, b) => {
    let av = a[key], bv = b[key];
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
    if (typeof av === 'boolean' && typeof bv === 'boolean') return dir === 'asc' ? (av === bv ? 0 : av ? -1 : 1) : (av === bv ? 0 : av ? 1 : -1);
    return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
}
